import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, Typography, Paper, Autocomplete, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Switch, CircularProgress
} from '@mui/material';
import { DepartmentService } from '../../services/department.service';
import { UserService } from '../../services/user.service';
import { Department, DepartmentUser } from '../../interfaces/department.interface';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';

interface InstitutionUser {
    id: string;
    username: string;
    phone: string;
    type: string;
    isActive: boolean;
}

const DepartmentUserAssignment: React.FC = () => {
    const { departmentId } = useParams<{ departmentId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const toast = useToast();
    const { t } = useTranslation('department');

    const isInstitutionAdmin = user?.roles.includes(UserType.InstitutionAdmin);

    const [department, setDepartment] = useState<Department | null>(null);
    const [departmentUsers, setDepartmentUsers] = useState<DepartmentUser[]>([]);
    const [searchedUsers, setSearchedUsers] = useState<InstitutionUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<InstitutionUser[]>([]);
    const [userInput, setUserInput] = useState('');
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (departmentId) {
            fetchData();
        }
    }, [departmentId]);

    useEffect(() => {
        if (!userInput.trim()) {
            setSearchedUsers([]);
            return;
        }
        const handle = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                const res = await UserService.getInstitutionUsers(0, 20, undefined, userInput);
                setSearchedUsers(res.content || []);
            } catch (err) {
                toast.showApiError(err, t('assignment.searchFailed'));
            } finally {
                setUserSearchLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInput]);

    const fetchData = async () => {
        try {
            const [dept, deptUsers] = await Promise.all([
                DepartmentService.getDepartmentById(departmentId!),
                DepartmentService.getDepartmentUsers(departmentId!),
            ]);
            setDepartment(dept);
            setDepartmentUsers(deptUsers);

            const preSelected: InstitutionUser[] = deptUsers.map((du: DepartmentUser) => ({
                id: du.userId,
                username: du.username || du.userId,
                phone: '',
                type: '',
                isActive: true,
            }));
            setSelectedUsers(preSelected);
        } catch (err) {
            toast.showApiError(err, t('assignment.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const userIds = selectedUsers.map(u => u.id);
            const updatedUsers = await DepartmentService.updateDepartmentUsers(departmentId!, userIds);
            setDepartmentUsers(updatedUsers);
            toast.showSuccess(t('assignment.updated'));
        } catch (err) {
            toast.showApiError(err, t('assignment.updateFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleToggleAdmin = async (userId: string) => {
        try {
            const updated = await DepartmentService.toggleDepartmentAdmin(departmentId!, userId);
            setDepartmentUsers(prev =>
                prev.map(du => du.userId === userId ? { ...du, isDepartmentAdmin: updated.isDepartmentAdmin } : du)
            );
        } catch (err) {
            toast.showApiError(err, t('assignment.toggleAdminFailed'));
        }
    };

    const getUserName = (userId: string): string => {
        const found = departmentUsers.find(du => du.userId === userId);
        return found && found.username ? displayUsername(found.username) : userId;
    };

    const autocompleteOptions = React.useMemo(() => {
        const merged = [...selectedUsers];
        searchedUsers.forEach(u => {
            if (!merged.find(s => s.id === u.id)) {
                merged.push(u);
            }
        });
        return merged;
    }, [selectedUsers, searchedUsers]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">
                    {department ? t('assignment.titleWithName', { name: department.name }) : t('assignment.title')}
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/departments')}>
                    {t('assignment.back')}
                </Button>
            </Box>

            <Paper sx={{ padding: 3, marginBottom: 3 }}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>{t('assignment.assignUsers')}</Typography>
                <Autocomplete
                    multiple
                    options={autocompleteOptions}
                    getOptionLabel={(option) => displayUsername(option.username)}
                    value={selectedUsers}
                    onChange={(_, newValue) => setSelectedUsers(newValue)}
                    onInputChange={(_, value, reason) => {
                        if (reason === 'input') setUserInput(value);
                        if (reason === 'clear') setUserInput('');
                    }}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    loading={userSearchLoading}
                    noOptionsText={userInput ? t('assignment.noUsers') : t('assignment.typeToSearch')}
                    renderInput={(params) => (
                        <TextField {...params} label={t('assignment.selectUsers')} placeholder={t('assignment.searchPlaceholder')} />
                    )}
                    sx={{ marginBottom: 2 }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? t('assignment.saving') : t('assignment.save')}
                </Button>
            </Paper>

            {departmentUsers.length > 0 && (
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>{t('assignment.currentMembers')}</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('assignment.username')}</TableCell>
                                    {isInstitutionAdmin && <TableCell>{t('assignment.departmentAdmin')}</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {departmentUsers.map((du) => (
                                    <TableRow key={du.id}>
                                        <TableCell>{getUserName(du.userId)}</TableCell>
                                        {isInstitutionAdmin && (
                                            <TableCell>
                                                <Switch
                                                    checked={du.isDepartmentAdmin}
                                                    onChange={() => handleToggleAdmin(du.userId)}
                                                    color="primary"
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
};

export default DepartmentUserAssignment;
